import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const guidesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const guideArticles = [
    { label: 'Complete Guide', path: '/pandabuy-spreadsheet-guide' },
    { label: 'Shoes Spreadsheet', path: '/articles/pandabuy-shoes-spreadsheet' },
    { label: 'Hoodies Spreadsheet', path: '/articles/pandabuy-hoodies-spreadsheet' },
    { label: 'T-Shirts Spreadsheet', path: '/articles/pandabuy-tshirts-spreadsheet' },
    { label: 'Jackets Spreadsheet', path: '/articles/pandabuy-jackets-spreadsheet' },
    { label: 'Pants Spreadsheet', path: '/articles/pandabuy-pants-spreadsheet' },
    { label: 'Accessories Spreadsheet', path: '/articles/pandabuy-accessories-spreadsheet' },
    { label: 'Bags Spreadsheet', path: '/articles/pandabuy-bags-spreadsheet' },
    { label: 'Sets Spreadsheet', path: '/articles/pandabuy-sets-spreadsheet' },
    { label: 'Jersey Spreadsheet', path: '/articles/pandabuy-jersey-spreadsheet' },
  ];

  const scrollToSection = useCallback((id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const offset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
    setMobileOpen(false);
  }, [location.pathname, navigate]);

  const linkClass = scrolled
    ? 'text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap'
    : 'text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap';

  const brandClass = scrolled
    ? 'flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 whitespace-nowrap'
    : 'flex items-center gap-2 font-heading text-xl font-bold text-white whitespace-nowrap';

  const menuBtnClass = scrolled
    ? 'md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer'
    : 'md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'navbar-scrolled border-b border-background-200/60' : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className={brandClass}>
            <span className="w-8 h-8 flex items-center justify-center bg-primary-500 text-background-50 rounded-lg text-sm font-bold">
              P
            </span>
            pandabuy<span className={scrolled ? 'text-primary-500' : 'text-primary-300'}> .team</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={linkClass}>
              Home
            </Link>
            <button onClick={() => scrollToSection('categories')} className={linkClass + ' cursor-pointer'}>
              Categories
            </button>
            <button onClick={() => scrollToSection('what-is')} className={linkClass + ' cursor-pointer'}>
              About
            </button>

            <div className="relative" ref={guidesRef}>
              <button
                onClick={() => setGuidesOpen(!guidesOpen)}
                className={`${linkClass} flex items-center gap-1 cursor-pointer`}
              >
                Shopping Guides
                {guidesOpen ? (
                  <i className="ri-arrow-up-s-line text-xs"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-xs"></i>
                )}
              </button>
              {guidesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-background-50 border border-background-200/60 rounded-lg overflow-hidden z-40 dropdown-animate shadow-lg">
                  {guideArticles.map((g) => (
                    <Link
                      key={g.path}
                      to={g.path}
                      onClick={() => setGuidesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
                    >
                      {g.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-1 inline-flex items-center px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              pandabuy sheet
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={menuBtnClass}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 dropdown-animate">
          <div className="px-4 py-3 flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
            <Link to="/" onClick={() => setMobileOpen(false)} className="text-sm font-semibold text-foreground-700 py-2 whitespace-nowrap">
              <i className="ri-home-line mr-2"></i>Home
            </Link>
            <button onClick={() => { scrollToSection('categories'); setMobileOpen(false); }} className="text-left text-sm font-medium text-foreground-700 py-2 whitespace-nowrap cursor-pointer">
              <i className="ri-grid-line mr-2"></i>Categories
            </button>
            <button onClick={() => { scrollToSection('what-is'); setMobileOpen(false); }} className="text-left text-sm font-medium text-foreground-700 py-2 whitespace-nowrap cursor-pointer">
              <i className="ri-information-line mr-2"></i>About pandabuyspreadsheet
            </button>
            <div className="border-t border-background-200/60 my-1"></div>
            <p className="text-xs font-semibold text-foreground-500 px-1 py-1">Shopping Guides</p>
            {guideArticles.map((g) => (
              <Link
                key={g.path}
                to={g.path}
                onClick={() => setMobileOpen(false)}
                className="text-sm text-foreground-600 py-1.5 pl-4 whitespace-nowrap hover:text-primary-500 transition-colors"
              >
                {g.label}
              </Link>
            ))}
            <div className="border-t border-background-200/60 my-1"></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              pandabuy sheet
              <i className="ri-arrow-right-up-line ml-1.5"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}